/**
 * Componente de Cards de Status da Fila PGMQ
 * 
 * Exibe métricas principais: pendentes, processando, completados, falhas,
 * taxa de sucesso e latência média.
 */

import { Clock, CheckCircle, XCircle, Loader2, Timer, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useFlowQueueStatsPgmq } from '@/hooks/useFlowQueueMonitorPgmq';

export function PgmqStatusCards() {
  const { data: stats, isLoading } = useFlowQueueStatsPgmq();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: 'Pendentes',
      value: stats?.pending || 0,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
    },
    {
      title: 'Processando',
      value: stats?.processing || 0,
      icon: Loader2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      iconClass: 'animate-spin',
    },
    {
      title: 'Completados (24h)',
      value: stats?.completed24h || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900/20',
    },
    {
      title: 'Falhas (24h)',
      value: stats?.failed24h || 0,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-100 dark:bg-red-900/20',
    },
    {
      title: 'Taxa de Sucesso',
      value: `${(stats?.successRate || 100).toFixed(1)}%`,
      icon: TrendingUp,
      color: (stats?.successRate || 100) >= 90 ? 'text-green-600' : 'text-red-600',
      bgColor: (stats?.successRate || 100) >= 90 
        ? 'bg-green-100 dark:bg-green-900/20' 
        : 'bg-red-100 dark:bg-red-900/20',
    },
    {
      title: 'Latência Média',
      value: `${((stats?.avgProcessingTime || 0) * 1000).toFixed(0)}ms`,
      icon: Timer,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {card.title}
                </p>
                <p className="text-2xl font-bold">{card.value}</p>
              </div>
              <div className={`p-3 rounded-full ${card.bgColor}`}>
                <card.icon className={`h-5 w-5 ${card.color} ${card.iconClass || ''}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
